"use client";

import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

interface PrivacyPolicyContentProps {
  lastUpdated: string;
  structuredData: Record<string, unknown>;
}

export default function PrivacyPolicyContent({
  lastUpdated,
  structuredData,
}: PrivacyPolicyContentProps) {
  return (
    <>
      {/* Structured Data */}
      <script
        // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
        type="application/ld+json"
      />

      <div className="min-h-screen bg-background">
        {/* Header Section */}
        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-b from-muted/30 to-background py-16 md:py-24"
          initial={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-4xl text-center">
              <Badge className="mb-4" variant="secondary">
                Legal Information
              </Badge>
              <h1 className="heading-display mb-6 font-bold font-heading text-4xl text-foreground md:text-5xl lg:text-6xl">
                Privacy Policy
              </h1>
              <p className="body-text-base mx-auto mb-8 max-w-2xl text-muted-foreground text-optimal">
                Your privacy matters to us. Learn how we collect, use, and
                protect your personal information in accordance with UK GDPR.
              </p>
              <div className="flex items-center justify-center gap-2 text-muted-foreground text-sm">
                <span>Last updated:</span>
                <time dateTime="2025-01-17">{lastUpdated}</time>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Content Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="mx-auto max-w-4xl">
            {/* Business Information */}
            <motion.div
              className="mb-12"
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              whileInView={{ opacity: 1, y: 0 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading text-2xl">
                    Contact Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <h4 className="mb-2 font-medium text-foreground">
                        Business Details
                      </h4>
                      <div className="space-y-1 text-muted-foreground text-sm">
                        <p>WillPower Store</p>
                        <p>13 Great Western Arcade</p>
                        <p>Birmingham, B32QD</p>
                        <p>United Kingdom</p>
                      </div>
                    </div>
                    <div>
                      <h4 className="mb-2 font-medium text-foreground">
                        Contact Details
                      </h4>
                      <div className="space-y-1 text-muted-foreground text-sm">
                        <p>Email: willpowerwellbeing@hotmail.com</p>
                        <p>Instagram: @willpowergymstoreuk</p>
                        <p>Website: willpower-store.com</p>
                        <p>Established: 2014</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Privacy Policy Sections */}
            <div className="space-y-12">
              {/* Section 1: Who We Are */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                whileInView={{ opacity: 1, y: 0 }}
              >
                <h2 className="mb-6 font-heading font-semibold text-2xl text-foreground md:text-3xl">
                  1. Who We Are
                </h2>
                <div className="prose prose-neutral max-w-none">
                  <p className="text-muted-foreground text-optimal leading-relaxed">
                    We are WillPower Store, a supplement retailer operating from
                    Birmingham city centre since 2014. We operate an e-commerce
                    website at willpower-store.com, selling quality supplements
                    from trusted third-party brands to customers primarily in
                    the UK. Our business is built on the values of speed,
                    reliability, and friendly customer service.
                  </p>
                </div>
              </motion.section>

              <Separator />

              {/* Section 2: What Personal Data We Collect */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                whileInView={{ opacity: 1, y: 0 }}
              >
                <h2 className="mb-6 font-heading font-semibold text-2xl text-foreground md:text-3xl">
                  2. What Personal Data We Collect
                </h2>
                <div className="space-y-6">
                  <p className="text-muted-foreground text-optimal leading-relaxed">
                    We collect the following personal information when you:
                  </p>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Card>
                      <CardContent className="pt-6">
                        <h4 className="mb-3 font-medium text-foreground">
                          When You Place an Order
                        </h4>
                        <ul className="space-y-1 text-muted-foreground text-sm">
                          <li>• Name and contact details</li>
                          <li>• Email address</li>
                          <li>• Billing and delivery addresses</li>
                          <li>• Phone number</li>
                          <li>• Payment information</li>
                        </ul>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <h4 className="mb-3 font-medium text-foreground">
                          When You Visit Our Website
                        </h4>
                        <ul className="space-y-1 text-muted-foreground text-sm">
                          <li>• IP address and location data</li>
                          <li>• Browser type and device information</li>
                          <li>• Cookies and tracking data</li>
                          <li>• Pages visited and time spent</li>
                          <li>• Search queries and preferences</li>
                        </ul>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </motion.section>

              <Separator />

              {/* Section 3: How We Use Your Data */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                whileInView={{ opacity: 1, y: 0 }}
              >
                <h2 className="mb-6 font-heading font-semibold text-2xl text-foreground md:text-3xl">
                  3. How We Use Your Data
                </h2>
                <div className="space-y-6">
                  <p className="text-muted-foreground text-optimal leading-relaxed">
                    We use your personal data to provide our services and
                    improve your experience:
                  </p>
                  <div className="grid gap-4">
                    <Card>
                      <CardContent className="pt-6">
                        <div className="grid gap-4 md:grid-cols-2">
                          <div>
                            <h4 className="mb-3 font-medium text-foreground">
                              Essential Services
                            </h4>
                            <ul className="space-y-1 text-muted-foreground text-sm">
                              <li>• Process and fulfil your orders</li>
                              <li>• Send order confirmations and updates</li>
                              <li>• Provide customer support</li>
                              <li>• Comply with legal obligations</li>
                            </ul>
                          </div>
                          <div>
                            <h4 className="mb-3 font-medium text-foreground">
                              Improvements & Marketing
                            </h4>
                            <ul className="space-y-1 text-muted-foreground text-sm">
                              <li>• Improve our website and services</li>
                              <li>
                                • Send marketing communications (with consent)
                              </li>
                              <li>• Personalise your shopping experience</li>
                              <li>• Analyse website usage and performance</li>
                            </ul>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="rounded-lg bg-muted/30 p-4">
                    <p className="text-muted-foreground text-sm">
                      <strong>Legal basis:</strong> Contract performance,
                      legitimate business interests, legal compliance, and
                      consent for marketing communications.
                    </p>
                  </div>
                </div>
              </motion.section>

              <Separator />

              {/* Section 4: Shopify and Third-Party Processing */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                whileInView={{ opacity: 1, y: 0 }}
              >
                <h2 className="mb-6 font-heading font-semibold text-2xl text-foreground md:text-3xl">
                  4. Shopify and Third-Party Processing
                </h2>
                <div className="space-y-6">
                  <p className="text-muted-foreground text-optimal leading-relaxed">
                    Our website is powered by Shopify Inc. and uses various
                    third-party services to provide you with the best possible
                    experience:
                  </p>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Card>
                      <CardContent className="pt-6">
                        <h4 className="mb-3 font-medium text-foreground">
                          E-commerce Platform
                        </h4>
                        <ul className="space-y-2 text-muted-foreground text-sm">
                          <li>
                            <strong>Shopify Inc.</strong> - E-commerce platform
                            and payment processing
                          </li>
                          <li>
                            <strong>Shopify Payments</strong> - Secure payment
                            processing
                          </li>
                        </ul>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <h4 className="mb-3 font-medium text-foreground">
                          Analytics & Performance
                        </h4>
                        <ul className="space-y-2 text-muted-foreground text-sm">
                          <li>
                            <strong>Vercel Analytics</strong> - Website
                            analytics and performance monitoring
                          </li>
                          <li>
                            <strong>Vercel</strong> - Website hosting and
                            performance optimisation
                          </li>
                          <li>
                            <strong>OpenTelemetry</strong> - Application
                            performance monitoring
                          </li>
                        </ul>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="rounded-lg bg-muted/30 p-4">
                    <p className="text-muted-foreground text-sm">
                      These services have their own privacy policies which
                      govern their use of your data. We ensure all third-party
                      processors meet appropriate data protection standards.
                    </p>
                  </div>
                </div>
              </motion.section>

              <Separator />

              {/* Section 5: Data Sharing */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                whileInView={{ opacity: 1, y: 0 }}
              >
                <h2 className="mb-6 font-heading font-semibold text-2xl text-foreground md:text-3xl">
                  5. Data Sharing
                </h2>
                <div className="space-y-6">
                  <p className="text-muted-foreground text-optimal leading-relaxed">
                    We may share your data with trusted partners and service
                    providers to deliver our services:
                  </p>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="grid gap-6 md:grid-cols-2">
                        <div>
                          <h4 className="mb-3 font-medium text-foreground">
                            Service Providers
                          </h4>
                          <ul className="space-y-1 text-muted-foreground text-sm">
                            <li>• Shopify (e-commerce platform)</li>
                            <li>• Shopify Payments (payment processing)</li>
                            <li>• Delivery companies (when available)</li>
                          </ul>
                        </div>
                        <div>
                          <h4 className="mb-3 font-medium text-foreground">
                            Legal Requirements
                          </h4>
                          <ul className="space-y-1 text-muted-foreground text-sm">
                            <li>• Legal authorities when required by law</li>
                            <li>• Fraud prevention and security</li>
                            <li>• Compliance with regulations</li>
                            <li>• Protection of rights and safety</li>
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </motion.section>

              <Separator />

              {/* Section 6: Your Rights Under UK GDPR */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                whileInView={{ opacity: 1, y: 0 }}
              >
                <h2 className="mb-6 font-heading font-semibold text-2xl text-foreground md:text-3xl">
                  6. Your Rights Under UK GDPR
                </h2>
                <div className="space-y-6">
                  <p className="text-muted-foreground text-optimal leading-relaxed">
                    You have important rights regarding your personal data:
                  </p>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Card>
                      <CardContent className="pt-6">
                        <h4 className="mb-3 font-medium text-foreground">
                          Access & Control
                        </h4>
                        <ul className="space-y-1 text-muted-foreground text-sm">
                          <li>• Access your personal data</li>
                          <li>• Rectify inaccurate data</li>
                          <li>• Erase your data (right to be forgotten)</li>
                          <li>• Restrict processing</li>
                        </ul>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <h4 className="mb-3 font-medium text-foreground">
                          Portability & Objection
                        </h4>
                        <ul className="space-y-1 text-muted-foreground text-sm">
                          <li>• Data portability</li>
                          <li>• Object to processing</li>
                          <li>• Withdraw consent at any time</li>
                          <li>• Lodge a complaint with the ICO</li>
                        </ul>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="rounded-lg bg-muted/30 p-4">
                    <p className="text-muted-foreground text-sm">
                      To exercise these rights, contact us at{" "}
                      <a
                        className="text-primary hover:underline"
                        href="mailto:willpowerwellbeing@hotmail.com"
                      >
                        willpowerwellbeing@hotmail.com
                      </a>
                    </p>
                  </div>
                </div>
              </motion.section>

              <Separator />

              {/* Section 7: Data Retention */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                whileInView={{ opacity: 1, y: 0 }}
              >
                <h2 className="mb-6 font-heading font-semibold text-2xl text-foreground md:text-3xl">
                  7. Data Retention
                </h2>
                <div className="space-y-6">
                  <p className="text-muted-foreground text-optimal leading-relaxed">
                    We retain your data for as long as necessary to fulfil the
                    purposes outlined above:
                  </p>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="grid gap-6 md:grid-cols-3">
                        <div>
                          <h4 className="mb-3 font-medium text-foreground">
                            Order Data
                          </h4>
                          <p className="text-muted-foreground text-sm">
                            7 years (for tax and accounting purposes)
                          </p>
                        </div>
                        <div>
                          <h4 className="mb-3 font-medium text-foreground">
                            Marketing Data
                          </h4>
                          <p className="text-muted-foreground text-sm">
                            Until you unsubscribe or withdraw consent
                          </p>
                        </div>
                        <div>
                          <h4 className="mb-3 font-medium text-foreground">
                            Website Analytics
                          </h4>
                          <p className="text-muted-foreground text-sm">
                            24 months (Vercel Analytics default)
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </motion.section>

              <Separator />

              {/* Section 8: Cookies */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                whileInView={{ opacity: 1, y: 0 }}
              >
                <h2 className="mb-6 font-heading font-semibold text-2xl text-foreground md:text-3xl">
                  8. Cookies
                </h2>
                <div className="space-y-6">
                  <p className="text-muted-foreground text-optimal leading-relaxed">
                    Our website uses cookies to enhance your experience:
                  </p>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Card>
                      <CardContent className="pt-6">
                        <h4 className="mb-3 font-medium text-foreground">
                          Essential Cookies
                        </h4>
                        <p className="mb-3 text-muted-foreground text-sm">
                          Necessary for website functionality and cannot be
                          disabled.
                        </p>
                        <ul className="space-y-1 text-muted-foreground text-sm">
                          <li>• Shopping cart functionality</li>
                          <li>• User authentication</li>
                          <li>• Security features</li>
                        </ul>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <h4 className="mb-3 font-medium text-foreground">
                          Analytics & Marketing
                        </h4>
                        <p className="mb-3 text-muted-foreground text-sm">
                          Used with your consent to improve our services.
                        </p>
                        <ul className="space-y-1 text-muted-foreground text-sm">
                          <li>• Website analytics</li>
                          <li>• Performance monitoring</li>
                          <li>• Personalised content</li>
                        </ul>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="rounded-lg bg-muted/30 p-4">
                    <p className="text-muted-foreground text-sm">
                      You can manage cookie preferences through our cookie
                      banner or your browser settings. For more details, see our{" "}
                      <a
                        className="text-primary hover:underline"
                        href="/cookies"
                      >
                        Cookie Policy
                      </a>
                      .
                    </p>
                  </div>
                </div>
              </motion.section>

              <Separator />

              {/* Section 9: Security */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                whileInView={{ opacity: 1, y: 0 }}
              >
                <h2 className="mb-6 font-heading font-semibold text-2xl text-foreground md:text-3xl">
                  9. Security
                </h2>
                <div className="space-y-6">
                  <p className="text-muted-foreground text-optimal leading-relaxed">
                    We implement appropriate security measures to protect your
                    data:
                  </p>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="grid gap-6 md:grid-cols-2">
                        <div>
                          <h4 className="mb-3 font-medium text-foreground">
                            Technical Measures
                          </h4>
                          <ul className="space-y-1 text-muted-foreground text-sm">
                            <li>• SSL encryption for data transmission</li>
                            <li>• Secure data storage and backup</li>
                            <li>• Regular security updates</li>
                            <li>• Access controls and monitoring</li>
                          </ul>
                        </div>
                        <div>
                          <h4 className="mb-3 font-medium text-foreground">
                            Organisational Measures
                          </h4>
                          <ul className="space-y-1 text-muted-foreground text-sm">
                            <li>• Staff training on data protection</li>
                            <li>• Data processing agreements</li>
                            <li>• Regular security assessments</li>
                            <li>• Incident response procedures</li>
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </motion.section>
            </div>

            {/* Contact Section */}
            <motion.div
              className="mt-16 text-center"
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              whileInView={{ opacity: 1, y: 0 }}
            >
              <Card>
                <CardContent className="pt-8 pb-8">
                  <h3 className="mb-4 font-heading font-medium text-foreground text-xl">
                    Questions About Your Privacy?
                  </h3>
                  <p className="mx-auto mb-6 max-w-2xl text-muted-foreground text-optimal">
                    If you have any questions about this privacy policy or how
                    we handle your personal data, please contact us via email or
                    Instagram.
                  </p>
                  <div className="flex flex-col justify-center gap-4 sm:flex-row">
                    <Button asChild size="lg">
                      <a href="mailto:willpowerwellbeing@hotmail.com">
                        Email Us
                      </a>
                    </Button>
                    <Button asChild size="lg" variant="outline">
                      <a
                        href="https://instagram.com/willpowergymstoreuk"
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        Instagram DM
                      </a>
                    </Button>
                  </div>
                  <div className="mx-auto mt-4 max-w-lg text-muted-foreground text-sm">
                    <p>
                      <strong>Response Times:</strong> We usually reply within
                      48 hours for both email and Instagram messages.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
}
